import { computed, ref } from 'vue';
import { useIsFetching, useQueries, useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';

import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { useBaseMenuStore } from '@/common/stores/base-menu';

import { getV8Dashboards } from '@/api-client/metav8Over/queries/integrated-dashboard-v8-controller/integrated-dashboard-v8-controller';
import { getV11HistoryMenu } from '@/api-client/userAndPermission/queries/history-controller/history-controller';
import { getV11DashboardsPermission } from '@/api-client/dashboard/queries/dashboard-v11-controller/dashboard-v11-controller';
import { usePostV6DashboardsFavorite } from '@/api-client/dashboard/queries/integrated-dashboard-controller/integrated-dashboard-controller';

import { UpdateFavoriteParams } from './menuSearch.type';
import { dashboardListKey } from './menuSearch.define';

export const useMenuSearchDefault = () => {
  const baseMenuStore = useBaseMenuStore();
  const { isShowMenuSearch } = storeToRefs(baseMenuStore);

  const queries = useQueries({
    queries: [
      {
        queryKey: ['v8-dashboards-dashboard', { includeImage: false }],
        queryFn: async () => {
          const response = await getV8Dashboards({
            includeImage: false,
            withFeatured: true,
          });
          return response.data.data || [];
        },
        meta: {
          frameName: FRAME_NAMES.DASHBOARD.DASHBOARD_LIST,
        },
        refetchOnMount: false,
        enabled: () => isShowMenuSearch.value,
      },
      {
        queryKey: ['v11-history-menu'],
        queryFn: async () => {
          const response = await getV11HistoryMenu();
          return response.data.data || [];
        },
        meta: {
          frameName: FRAME_NAMES.GNB.MENU_SEARCH_RECENT,
        },
        refetchOnMount: false,
        enabled: () => isShowMenuSearch.value,
      },
    ],
  });

  // 조회 완료 여부
  const isFetched = computed(() => queries.value.every((query) => query.isFetched));
  // 에러 발생 여부
  const isError = computed(() => queries.value.some((query) => query.isError));

  // 개별 쿼리 에러 상태
  const isDashboardListError = computed(() => queries.value[0]?.isError ?? false);
  const isHistoryMenuError = computed(() => queries.value[1]?.isError ?? false);

  const dashboardList = computed(() => queries.value[0]?.data || []);
  const menuSearchRecentData = computed(() => queries.value[1]?.data || []);

  return {
    isFetched,
    isError,
    isDashboardListError,
    isHistoryMenuError,

    dashboardList,
    menuSearchRecentData,
  };
};

export const useMenuSearchPermission = () => {
  const queryClient = useQueryClient();

  const getDashboardPermission = (dashboardId: number) => {
    return queryClient.fetchQuery({
      queryKey: ['dashboardPermission', dashboardId],
      queryFn: async () => {
        const { data: response } = await getV11DashboardsPermission({ dashboardId });
        return response?.data?.[0]?.code ?? null;
      },
    });
  };

  return { getDashboardPermission };
};

export const useFavoriteDashboardsMutation = () => {
  const queryClient = useQueryClient();
  const postFavoriteMutation = usePostV6DashboardsFavorite();

  const fetchingCount = useIsFetching({ queryKey: dashboardListKey });
  const isDashboardListFetching = computed(() => fetchingCount.value > 0);
  const isClickDashboard = ref(false);

  const updateFavoriteDashboard = async ({ dashboardId, favoriteId }: UpdateFavoriteParams) => {
    await postFavoriteMutation.mutateAsync({
      data: { dashboards: [{ dashboardId, favoriteId, deleted: !!favoriteId }] },
    });

    await queryClient.refetchQueries({ queryKey: dashboardListKey });

    isClickDashboard.value = true;
  };

  const isPending = computed(
    () => postFavoriteMutation.isPending.value || isDashboardListFetching.value,
  );

  const resetClickDashboard = () => {
    isClickDashboard.value = false;
  };

  return {
    isPending,
    isClickDashboard,
    isSuccess: postFavoriteMutation.isSuccess,
    error: postFavoriteMutation.error,

    resetClickDashboard,
    updateFavoriteDashboard,
  };
};
